// Game orchestrator: fixed-timestep sim loop, AI, renderer, input, selection, targeting.
// The React HUD reads this object (read-only) and calls its intent methods.

import { AiDirector } from '../ai/director';
import { AudioDirector, type OrderCue } from './audio';
import { DEFAULT_SETTINGS, type SettingsData } from './persistence';
import { FACTIONS, type MatchSetup } from '../content/factions';
import { buildingDef, cardDef, ritualDef, spellDef, trapDef, unitDef } from '../content/registry';
import { TUNING } from '../content/tuning';
import type { Vec2 } from '../shared/vec';
import { dist, vec } from '../shared/vec';
import { GameRenderer, type OrderMarkerKind, type SelectionView } from '../render/renderer';
import { Sim } from '../sim/sim';
import { isDebugCommand, type PlayerCommand, type SimCommand } from '../sim/commands';
import { validatePlacement } from '../sim/systems/cards';
import { validRitualPos } from '../sim/systems/rituals';
import type { EntId, HLKind, SimEvent } from '../sim/state';
import { footprintOf } from '../sim/state';
import { OnlineClient, OnlineMatchRuntime } from '../net/runtime';
import { isAnyBaseUnderAttack } from './music';
import type { PlayerId } from '../content/types';

export interface Alert {
  id: number;
  text: string;
  at: number; // wall time seconds
  pos?: Vec2;
}

export type PendingMode =
  | { kind: 'none' }
  | { kind: 'card'; cardId: string }
  | { kind: 'command'; command: HLKind };

/** squad-order → click-marker color family (classic RTS: green move, red attack) */
const MARKER_BY_COMMAND: Record<HLKind, OrderMarkerKind> = {
  idle: 'move',
  move: 'move',
  attack: 'attack',
  capture: 'capture',
  defend: 'rally',
  patrol: 'rally',
  raid: 'attack',
  escort: 'escort',
  withdraw: 'withdraw',
};

const AUDIO_ORDER_BY_COMMAND: Record<HLKind, OrderCue> = {
  idle: 'move',
  move: 'move',
  attack: 'attack',
  capture: 'capture',
  defend: 'defend',
  patrol: 'defend',
  raid: 'attack',
  escort: 'mount',
  withdraw: 'withdraw',
};

export class Game {
  sim: Sim;
  ai: AiDirector[];
  onlineRuntime: OnlineMatchRuntime | null = null;
  readonly localPlayer: PlayerId;
  renderer: GameRenderer;
  readonly audio: AudioDirector;
  selection: SelectionView = { squads: [], workers: [], buildingId: null, inspect: null };
  pending: PendingMode = { kind: 'none' };
  alerts: Alert[] = [];
  hoverWorld: Vec2 | null = null;
  /** last cursor position over the canvas, in client px — drives edge scrolling */
  private pointerClient: { x: number; y: number } | null = null;
  boxStart: { x: number; y: number } | null = null;
  boxCurrent: { x: number; y: number } | null = null;
  lastDenyReason = '';
  lastDenyAt = 0;
  fps = 0;

  private accumulator = 0;
  private lastFrame = 0;
  private rafId = 0;
  private keys = new Set<string>();
  private alertSeq = 1;
  private disposed = false;
  private renderAlpha = 1;
  private ownsAudio = false;
  private lastMusicThreatCheck = -Infinity;

  settings: SettingsData;

  constructor(
    private container: HTMLElement,
    seed: number,
    setup?: MatchSetup,
    settings?: SettingsData,
    online?: { client: OnlineClient; matchId: string; localPlayer: PlayerId },
    audio?: AudioDirector,
  ) {
    this.settings = settings ?? { ...DEFAULT_SETTINGS };
    this.localPlayer = online?.localPlayer ?? 0;
    const ic = FACTIONS['iron-covenant'];
    this.sim = new Sim(
      seed,
      setup ?? {
        mode: '1v1',
        mapId: 'crownline-crossing',
        players: [
          { faction: 'iron-covenant', deck: [...ic.defaultDeck], isAI: false, team: 0 },
          { faction: 'iron-covenant', deck: [...ic.defaultDeck], isAI: true, team: 1 },
        ],
      },
    );
    this.ai = online || setup?.tutorial
      ? []
      : this.sim.state.players
          .filter((player) => player.isAI)
          .map((player) => new AiDirector(this.sim, player.id, setup?.players[player.id]?.aiDifficulty ?? 'standard'));
    if (online) {
      this.onlineRuntime = new OnlineMatchRuntime(this.sim, online.client, online.matchId, () => this.consumeEvents(this.sim.state.events));
    }
    this.renderer = new GameRenderer(container, this.sim);
    this.moveCameraTo(this.sim.map.corePositions[this.localPlayer]);
    this.audio = audio ?? new AudioDirector(this.settings);
    this.ownsAudio = audio === undefined;
    this.applySettings(this.settings);
    this.bindInput();
    this.lastFrame = performance.now();
    this.loop(this.lastFrame);
  }

  dispose(): void {
    this.disposed = true;
    cancelAnimationFrame(this.rafId);
    this.unbindInput();
    this.onlineRuntime?.dispose();
    if (this.ownsAudio) this.audio.dispose();
    this.renderer.dispose();
  }

  // ---------------------------------------------------------------- loop

  private loop = (now: number): void => {
    if (this.disposed) return;
    this.rafId = requestAnimationFrame(this.loop);
    const frameDt = Math.min(0.25, (now - this.lastFrame) / 1000);
    this.lastFrame = now;
    this.fps = this.fps * 0.95 + (1 / Math.max(1e-6, frameDt)) * 0.05;

    if (!this.sim.state.paused) {
      this.accumulator += frameDt;
      let steps = 0;
      while (this.accumulator >= TUNING.dt && steps < 8) {
        if (this.onlineRuntime) {
          // The server advances the simulation. Snapshots arrive independently and
          // update the local read model used by the renderer/HUD.
          this.accumulator = 0;
          break;
        }
        this.sim.tick();
        for (const director of this.ai) director.update();
        this.consumeEvents(this.sim.state.events);
        this.accumulator -= TUNING.dt;
        steps++;
      }
    }

    // camera pan (arrow keys)
    // Scale from the *smoothed* zoom, not the desired one: holding an arrow key while the zoom
    // is still easing would otherwise pan at a speed that doesn't match what's on screen.
    const panSpeed = (this.renderer.smoothedZoom * 0.9 + 12) * frameDt * this.settings.panSpeed;
    if (this.keys.has('arrowup')) this.renderer.camTarget.y -= panSpeed;
    if (this.keys.has('arrowdown')) this.renderer.camTarget.y += panSpeed;
    if (this.keys.has('arrowleft')) this.renderer.camTarget.x -= panSpeed;
    if (this.keys.has('arrowright')) this.renderer.camTarget.x += panSpeed;

    // edge scrolling (Gameplay setting): pan while the cursor rests near a screen edge.
    // Suppressed mid box-select so dragging a selection near the edge doesn't fight the camera.
    if (this.settings.edgeScroll && this.pointerClient && !this.boxStart) {
      const rect = this.container.getBoundingClientRect();
      // Wider band than the old 24px, because the speed now ramps across it instead of
      // switching on at full speed the instant the cursor crosses the threshold.
      const margin = 40; // TEST
      const px = this.pointerClient.x - rect.left;
      const py = this.pointerClient.y - rect.top;
      if (px >= 0 && px <= rect.width && py >= 0 && py <= rect.height) {
        // Quadratic ease-in over the margin: a gentle creep near the inner edge building to full
        // speed at the screen edge, rather than an on/off switch.
        const ramp = (distanceToEdge: number): number => {
          const t = Math.max(0, Math.min(1, (margin - distanceToEdge) / margin));
          return t * t;
        };
        if (px < margin) this.renderer.camTarget.x -= panSpeed * ramp(px);
        else if (px > rect.width - margin) this.renderer.camTarget.x += panSpeed * ramp(rect.width - px);
        if (py < margin) this.renderer.camTarget.y -= panSpeed * ramp(py);
        else if (py > rect.height - margin) this.renderer.camTarget.y += panSpeed * ramp(rect.height - py);
      }
    }

    // prune dead selection
    this.selection.squads = this.selection.squads.filter((id) => this.sim.state.squads.has(id));
    this.selection.workers = this.selection.workers.filter((id) => this.sim.state.workers.has(id));
    if (this.selection.buildingId !== null && !this.sim.state.buildings.has(this.selection.buildingId))
      this.selection.buildingId = null;
    const insp = this.selection.inspect;
    if (insp !== null) {
      const st = this.sim.state;
      const alive =
        insp.kind === 'enemySquad' ? st.squads.has(insp.id)
        : insp.kind === 'enemyBuilding' ? st.buildings.has(insp.id)
        : insp.kind === 'deposit' ? st.deposits.has(insp.id)
        : st.ruins.some((r) => r.id === insp.id && r.buildingId === null);
      if (!alive) this.selection.inspect = null;
    }

    // ghost preview for building/trap cards
    this.updateGhost();

    const alpha = this.onlineRuntime ? this.onlineRuntime.renderAlpha : Math.max(0, Math.min(1, this.accumulator / TUNING.dt));
    this.renderAlpha = alpha;
    this.renderer.update(alpha, frameDt, this.selection, this.hoverWorld);

    // expire alerts
    const wallNow = performance.now() / 1000;
    this.alerts = this.alerts.filter((a) => wallNow - a.at < 6);
    if (wallNow - this.lastMusicThreatCheck >= 0.25) {
      this.lastMusicThreatCheck = wallNow;
      this.audio.setBaseThreat(isAnyBaseUnderAttack(this.sim));
    }
  };

  private updateGhost(): void {
    if (this.pending.kind === 'card' && this.hoverWorld) {
      const def = cardDef(this.pending.cardId);
      if (def.category === 'building') {
        const bd = buildingDef(def.subjectId);
        const check = validatePlacement(this.sim, this.localPlayer, bd.footprint, this.hoverWorld, bd.isMajorStructure);
        this.renderer.setGhost(this.hoverWorld, bd.footprint, check.ok);
        return;
      }
      if (def.category === 'trap') {
        const td = trapDef(def.subjectId);
        const check = validatePlacement(this.sim, this.localPlayer, { w: 1, h: 1 }, this.hoverWorld, false);
        this.renderer.setGhost(this.hoverWorld, { w: td.triggerRadius * 2, h: td.triggerRadius * 2 }, check.ok);
        return;
      }
      if (def.category === 'unit' || def.category === 'boss') {
        const ok = this.sim.isValidSummonPos(this.hoverWorld, this.localPlayer, def.summonLocations, def.ascension);
        this.renderer.setGhost(this.hoverWorld, { w: 2, h: 2 }, ok);
        return;
      }
      if (def.category === 'ritual') {
        const rd = ritualDef(def.subjectId);
        const ok =
          validRitualPos(this.sim, this.localPlayer, rd, this.hoverWorld) &&
          validatePlacement(this.sim, this.localPlayer, { w: 2, h: 2 }, this.hoverWorld, false).ok;
        this.renderer.setGhost(this.hoverWorld, { w: 2, h: 2 }, ok);
        return;
      }
    }
    this.renderer.setGhost(null, null, false);
  }

  // ---------------------------------------------------------------- events → alerts

  private pushAlert(text: string, pos?: Vec2): void {
    this.alerts.push({ id: this.alertSeq++, text, at: performance.now() / 1000, pos });
    if (this.alerts.length > 4) this.alerts.shift();
  }

  private consumeEvents(events: SimEvent[]): void {
    this.renderer.addEvents(events);
    this.audio.events(events, this.localPlayer, this.sim.teamOf(this.localPlayer), this.renderer.camTarget, (event) => this.audioEventPosition(event));
    const relation = (player: PlayerId, self: string, ally: string, enemy: string) =>
      player === this.localPlayer ? self : this.sim.areAllies(player, this.localPlayer) ? ally : enemy;
    for (const e of events) {
      switch (e.type) {
        case 'SiteCaptured': {
          const site = this.sim.state.sites.find((s) => s.id === e.site);
          this.pushAlert(`${relation(e.owner, 'You', 'Ally', 'Enemy')} captured ${site?.name ?? e.site}`, site?.pos);
          break;
        }
        case 'SiteLost':
          if (e.loser === this.localPlayer) {
            const site = this.sim.state.sites.find((s) => s.id === e.site);
            this.pushAlert(`Site lost: ${site?.name ?? e.site}`, site?.pos);
          }
          break;
        case 'AscensionUnlocked':
          if (this.settings.alertVerbosity === 'all') {
            this.pushAlert(`${relation(e.player, 'You', 'Ally', 'Enemy')} reached Ascension ${['I', 'II', 'III', 'IV'][e.tier - 1]}`);
          }
          break;
        case 'BossSummoned':
          this.pushAlert(`${relation(e.player, 'Your', 'ALLY', 'ENEMY')} boss: ${unitDef(e.defId).name}!`, e.pos);
          break;
        case 'BossSummoning':
          if (e.global || this.sim.areAllies(e.player, this.localPlayer)) {
            this.pushAlert(
              `${relation(e.player, 'Your', 'ALLY', 'ENEMY')} ${unitDef(e.defId).name} assembling — ${Math.round(e.seconds)}s`,
              e.pos,
            );
          }
          break;
        case 'RitualStarted':
          this.pushAlert(`${relation(e.player, 'Your', 'Allied', 'Enemy')} ritual channeling — destroy the standard to stop it`, e.pos);
          break;
        case 'RitualComplete':
          this.pushAlert(`${relation(e.player, 'Your', 'Allied', 'Enemy')} ritual COMPLETE`, e.pos);
          break;
        case 'RitualCancelled':
          this.pushAlert(`${relation(e.player, 'Your', 'Allied', 'Enemy')} ritual interrupted`, e.pos);
          break;
        case 'CoreDamaged':
          if (e.player === this.localPlayer && this.sim.state.tick % 40 === 0) this.pushAlert('Core under attack!', this.sim.coreAlive(this.localPlayer)?.pos);
          break;
        case 'RaidWarning':
          if (e.player === this.localPlayer && this.sim.state.tick % 60 === 0 && this.settings.alertVerbosity === 'all') {
            this.pushAlert('Workers under attack!', e.pos);
          }
          break;
        case 'CardDenied':
          if (e.player === this.localPlayer) {
            this.lastDenyReason = e.reason;
            this.lastDenyAt = performance.now() / 1000;
          }
          break;
        case 'MatchEnded':
          this.pushAlert(e.winningTeam === -1 ? 'MATCH ABANDONED' : e.winningTeam === this.sim.teamOf(this.localPlayer) ? 'VICTORY — enemy team eliminated' : 'DEFEAT — your team was eliminated');
          break;
        default:
          break;
      }
    }
  }

  private audioEventPosition(event: SimEvent): Vec2 | null {
    switch (event.type) {
      case 'SiteCaptured':
      case 'SiteLost':
      case 'SiteNeutralized':
        return this.sim.state.sites.find((site) => site.id === event.site)?.pos ?? null;
      case 'BuildingComplete':
        return this.sim.state.buildings.get(event.id)?.pos ?? null;
      case 'SquadSpawned':
        return this.sim.state.squads.get(event.id)?.pos ?? null;
      case 'CoreDamaged':
        return this.sim.coreAlive(event.player)?.pos ?? this.sim.map.corePositions[event.player];
      default:
        return null;
    }
  }

  // ---------------------------------------------------------------- input

  private bindInput(): void {
    const canvas = this.renderer.renderer.domElement;
    canvas.addEventListener('mousedown', this.onMouseDown);
    canvas.addEventListener('mousemove', this.onMouseMove);
    canvas.addEventListener('mouseleave', this.onMouseLeave);
    window.addEventListener('mouseup', this.onMouseUp);
    canvas.addEventListener('contextmenu', (e) => e.preventDefault());
    canvas.addEventListener('wheel', this.onWheel, { passive: false });
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
  }

  private unbindInput(): void {
    const canvas = this.renderer.renderer.domElement;
    canvas.removeEventListener('mousedown', this.onMouseDown);
    canvas.removeEventListener('mousemove', this.onMouseMove);
    canvas.removeEventListener('mouseleave', this.onMouseLeave);
    window.removeEventListener('mouseup', this.onMouseUp);
    canvas.removeEventListener('wheel', this.onWheel);
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
  }

  private onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    const zoomIn = this.settings.zoomInvert ? e.deltaY > 0 : e.deltaY < 0;
    this.renderer.zoom *= zoomIn ? 0.89 : 1.12;
  };

  private onKeyDown = (e: KeyboardEvent): void => {
    if (e.repeat) return;
    const k = e.key.toLowerCase();
    if (k.startsWith('arrow')) {
      e.preventDefault();
      this.keys.add(k);
    }

    // card hotkeys: single row 1–9 then 0 (GDD §17.1, 10 slots)
    const slotKeys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];
    const slot = slotKeys.indexOf(k);
    if (slot >= 0) {
      const cardId = this.sim.state.players[this.localPlayer].deck[slot];
      if (cardId) this.beginCardTargeting(cardId, true);
    }
    // command letters (plan §25.5: A C D P R E W)
    switch (k) {
      case ' ':
        e.preventDefault();
        this.togglePause();
        break;
      case 'escape':
        this.pending = { kind: 'none' };
        this.selection = { squads: [], workers: [], buildingId: null, inspect: null };
        this.audio.ui('close');
        break;
      case 'f': {
        const core = this.sim.coreAlive(this.localPlayer);
        if (core) {
          this.renderer.camTarget.x = core.pos.x;
          this.renderer.camTarget.y = core.pos.y;
        }
        break;
      }
      case 'h': {
        // recall/resume the SELECTED zone only (owner request: no global recall-all)
        const b = this.selection.buildingId !== null ? this.sim.state.buildings.get(this.selection.buildingId) : undefined;
        if (b && b.owner === this.localPlayer && (b.isCore || buildingDef(b.defId).producesWorkers)) {
          this.dispatch(
            b.recallActive
              ? { kind: 'ResumeWork', player: 0, hubId: b.id }
              : { kind: 'RecallWorkers', player: 0, hubId: b.id },
          );
        }
        break;
      }
      case 'a':
        if (this.selection.squads.length) this.pending = { kind: 'command', command: 'attack' };
        break;
      case 'c':
        if (this.selection.squads.length) this.pending = { kind: 'command', command: 'capture' };
        break;
      case 'd':
        if (this.selection.squads.length) this.pending = { kind: 'command', command: 'defend' };
        break;
      case 'p':
        if (this.selection.squads.length) this.pending = { kind: 'command', command: 'patrol' };
        break;
      case 'r':
        if (this.selection.squads.length) this.pending = { kind: 'command', command: 'raid' };
        break;
      case 'e': {
        // Core Annex E-actions take priority over escort: deploy carriers / take root
        let acted = false;
        for (const id of this.selection.squads) {
          const s = this.sim.state.squads.get(id);
          if (!s || s.owner !== this.localPlayer) continue;
          const ud = unitDef(s.defId);
          if (ud.deploysInto) {
            this.dispatch({ kind: 'DeployCarrier', player: 0, squadId: id });
            acted = true;
          } else if (ud.rootable) {
            this.dispatch({ kind: 'ToggleRoot', player: 0, squadId: id });
            acted = true;
          }
        }
        if (!acted && this.selection.squads.length) this.pending = { kind: 'command', command: 'escort' };
        break;
      }
      case 'w':
        if (this.selection.squads.length) this.issueCommand('withdraw');
        break;
      default:
        break;
    }
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    this.keys.delete(e.key.toLowerCase());
  };

  private onMouseDown = (e: MouseEvent): void => {
    if (e.button === 0) {
      const world = this.renderer.screenToWorld(e.clientX, e.clientY);
      if (!world) return;
      if (this.pending.kind === 'card') {
        this.playPendingCard(world);
        return;
      }
      if (this.pending.kind === 'command') {
        this.issuePendingCommand(world);
        return;
      }
      this.boxStart = { x: e.clientX, y: e.clientY };
      this.boxCurrent = { x: e.clientX, y: e.clientY };
    } else if (e.button === 2) {
      const world = this.renderer.screenToWorld(e.clientX, e.clientY);
      if (!world) return;
      this.pending = { kind: 'none' };
      this.contextCommand(world);
    }
  };

  private onMouseMove = (e: MouseEvent): void => {
    this.hoverWorld = this.renderer.screenToWorld(e.clientX, e.clientY);
    this.pointerClient = { x: e.clientX, y: e.clientY };
    if (this.boxStart) this.boxCurrent = { x: e.clientX, y: e.clientY };
  };

  private onMouseLeave = (): void => {
    this.pointerClient = null;
    this.hoverWorld = null;
  };

  private onMouseUp = (e: MouseEvent): void => {
    if (e.button !== 0 || !this.boxStart) return;
    const start = this.boxStart;
    const end = { x: e.clientX, y: e.clientY };
    this.boxStart = null;
    this.boxCurrent = null;
    const dx = Math.abs(end.x - start.x);
    const dy = Math.abs(end.y - start.y);
    if (dx < 6 && dy < 6) {
      this.clickSelect(end.x, end.y, e.shiftKey);
    } else {
      this.boxSelect(start, end, e.shiftKey);
    }
    if (
      this.selection.squads.length > 0 ||
      this.selection.workers.length > 0 ||
      this.selection.buildingId !== null ||
      this.selection.inspect !== null
    ) this.audio.ui('select');
  };

  // ---------------------------------------------------------------- selection

  private clickSelect(sx: number, sy: number, additive: boolean): void {
    const world = this.renderer.screenToWorld(sx, sy);
    if (!world) return;
    if (!additive) this.selection = { squads: [], workers: [], buildingId: null, inspect: null };

    // squads first
    let best: EntId | null = null;
    let bestD = 2.2;
    for (const s of this.sim.state.squads.values()) {
      if (s.owner !== this.localPlayer) continue;
      const d = dist(this.visualPosition(s), world);
      if (d < bestD) {
        bestD = d;
        best = s.id;
      }
    }
    if (best !== null) {
      this.selection.inspect = null;
      if (additive && this.selection.squads.includes(best)) {
        this.selection.squads = this.selection.squads.filter((i) => i !== best);
      } else {
        this.selection.squads.push(best);
      }
      return;
    }
    // workers
    bestD = 1.4;
    for (const w of this.sim.state.workers.values()) {
      if (w.owner !== this.localPlayer || w.state === 'sheltered') continue;
      const d = dist(this.visualPosition(w), world);
      if (d < bestD) {
        bestD = d;
        best = w.id;
      }
    }
    if (best !== null) {
      this.selection.inspect = null;
      this.selection.workers.push(best);
      return;
    }
    // buildings (own)
    for (const b of this.sim.state.buildings.values()) {
      if (b.owner !== this.localPlayer) continue;
      const fp = footprintOf(buildingDef(b.defId), b.rot);
      if (
        Math.abs(world.x - b.pos.x) <= fp.w / 2 + 0.5 &&
        Math.abs(world.y - b.pos.y) <= fp.h / 2 + 0.5
      ) {
        this.selection.inspect = null;
        this.selection.buildingId = b.id;
        return;
      }
    }
    // nothing of ours hit — read-only inspection targets
    // enemy squad
    bestD = 2.2;
    for (const s of this.sim.state.squads.values()) {
      if (s.owner === this.localPlayer) continue;
      const d = dist(this.visualPosition(s), world);
      if (d < bestD) {
        bestD = d;
        best = s.id;
      }
    }
    if (best !== null) {
      this.selection.inspect = { kind: 'enemySquad', id: best };
      return;
    }
    // enemy building
    for (const b of this.sim.state.buildings.values()) {
      if (b.owner === this.localPlayer) continue;
      const fp = footprintOf(buildingDef(b.defId), b.rot);
      if (
        Math.abs(world.x - b.pos.x) <= fp.w / 2 + 0.5 &&
        Math.abs(world.y - b.pos.y) <= fp.h / 2 + 0.5
      ) {
        this.selection.inspect = { kind: 'enemyBuilding', id: b.id };
        return;
      }
    }
    // resource deposit
    for (const d of this.sim.state.deposits.values()) {
      if (dist(d.pos, world) <= 1.8) {
        this.selection.inspect = { kind: 'deposit', id: d.id };
        return;
      }
    }
    // unbuilt wall foundation (ruin)
    for (const r of this.sim.state.ruins) {
      if (r.buildingId === null && dist(r.pos, world) <= 2.5) {
        this.selection.inspect = { kind: 'ruin', id: r.id };
        return;
      }
    }
  }

  private boxSelect(a: { x: number; y: number }, b: { x: number; y: number }, additive: boolean): void {
    const minX = Math.min(a.x, b.x);
    const maxX = Math.max(a.x, b.x);
    const minY = Math.min(a.y, b.y);
    const maxY = Math.max(a.y, b.y);
    if (!additive) this.selection = { squads: [], workers: [], buildingId: null, inspect: null };
    const squads: EntId[] = [];
    for (const s of this.sim.state.squads.values()) {
      if (s.owner !== this.localPlayer) continue;
      const sc = this.renderer.worldToScreen(this.visualPosition(s));
      if (sc.x >= minX && sc.x <= maxX && sc.y >= minY && sc.y <= maxY) squads.push(s.id);
    }
    if (squads.length > 0) {
      this.selection.squads.push(...squads.filter((id) => !this.selection.squads.includes(id)));
      return;
    }
    for (const w of this.sim.state.workers.values()) {
      if (w.owner !== this.localPlayer || w.state === 'sheltered') continue;
      const sc = this.renderer.worldToScreen(this.visualPosition(w));
      if (sc.x >= minX && sc.x <= maxX && sc.y >= minY && sc.y <= maxY) {
        if (!this.selection.workers.includes(w.id)) this.selection.workers.push(w.id);
      }
    }
  }

  // ---------------------------------------------------------------- commands

  /** Card-button clicks always arm targeting; only battlefield hotkeys may quick-cast. */
  beginCardTargeting(cardId: string, fromHotkey = false): void {
    if (fromHotkey && this.settings.quickCast && this.pointerClient && this.hoverWorld) {
      this.pending = { kind: 'card', cardId };
      this.playPendingCard(this.hoverWorld);
      return;
    }
    this.audio.ui('select');
    this.pending = { kind: 'card', cardId };
  }

  applySettings(settings: SettingsData): void {
    this.settings = settings;
    this.audio?.setSettings(settings);
    this.renderer.applyPreferences(settings);
  }

  beginCommandTargeting(command: HLKind): void {
    if (command === 'withdraw') this.issueCommand('withdraw');
    else {
      this.audio.ui('select');
      this.pending = { kind: 'command', command };
    }
  }

  private playPendingCard(world: Vec2): void {
    if (this.pending.kind !== 'card') return;
    const cardId = this.pending.cardId;
    const def = cardDef(cardId);
    let targetEntity: EntId | undefined;
    if (def.category === 'spell') {
      const sd = spellDef(def.subjectId);
      if (sd.targeting.shape === 'entity') {
        // resolve the click to the nearest valid entity for the spell's target kind
        if (sd.targeting.validTargets === 'friendly-building') {
          let bestD = 8;
          for (const b of this.sim.state.buildings.values()) {
            if (!this.sim.areAllies(b.owner, this.localPlayer)) continue;
            const d = dist(b.pos, world);
            if (d < bestD) {
              bestD = d;
              targetEntity = b.id;
            }
          }
          if (targetEntity === undefined) {
            this.lastDenyReason = 'Target a friendly building';
            this.lastDenyAt = performance.now() / 1000;
            this.audio.ui('deny');
            return;
          }
        } else {
          // squad-target spells: nearest qualifying squad to the click
          const wantFriendly = sd.targeting.validTargets !== 'enemy-squad';
          let bestD = 6;
          for (const s of this.sim.state.squads.values()) {
            if (wantFriendly ? !this.sim.areAllies(s.owner, this.localPlayer) : !this.sim.areHostile(s.owner, this.localPlayer)) continue;
            const d = dist(this.visualPosition(s), world);
            if (d < bestD) {
              bestD = d;
              targetEntity = s.id;
            }
          }
          if (targetEntity === undefined) {
            this.lastDenyReason = wantFriendly ? 'Target a friendly squad' : 'Target an enemy squad';
            this.lastDenyAt = performance.now() / 1000;
            this.audio.ui('deny');
            return;
          }
        }
      }
    }
    this.dispatch({ kind: 'PlayCard', player: 0, cardId, targetPos: world, targetEntity });
    this.pending = { kind: 'none' };
  }

  private issuePendingCommand(world: Vec2): void {
    if (this.pending.kind !== 'command') return;
    const command = this.pending.command;
    this.pending = { kind: 'none' };
    let targetEntity: EntId | undefined;
    if (command === 'attack') targetEntity = this.pickEnemyEntity(world) ?? undefined;
    if (command === 'escort') {
      // nearest own boss
      let bestD = 10;
      for (const s of this.sim.state.squads.values()) {
        if (s.owner !== this.localPlayer || !s.isBoss) continue;
        const d = dist(this.visualPosition(s), world);
        if (d < bestD) {
          bestD = d;
          targetEntity = s.id;
        }
      }
      if (targetEntity === undefined) {
        this.lastDenyReason = 'Escort requires one of your bosses as target';
        this.lastDenyAt = performance.now() / 1000;
        this.audio.ui('deny');
        return;
      }
    }
    this.issueCommand(command, world, targetEntity);
  }

  issueCommand(command: HLKind, targetPos?: Vec2, targetEntity?: EntId): void {
    if (this.selection.squads.length === 0) return;
    if (targetPos) this.renderer.spawnOrderMarker(targetPos, MARKER_BY_COMMAND[command]);
    const sourcePos = this.sim.state.squads.get(this.selection.squads[0])?.pos ?? null;
    this.audio.order(AUDIO_ORDER_BY_COMMAND[command], targetPos ?? sourcePos, this.renderer.camTarget);
    this.dispatch({
      kind: 'IssueCommand',
      player: 0,
      squadIds: [...this.selection.squads],
      command,
      targetPos,
      targetEntity,
    });
  }

  private pickEnemyEntity(world: Vec2): EntId | null {
    let best: EntId | null = null;
    let bestD = 2.5;
    for (const s of this.sim.state.squads.values()) {
      if (!this.sim.areHostile(s.owner, this.localPlayer)) continue;
      const d = dist(this.visualPosition(s), world);
      if (d < bestD) {
        bestD = d;
        best = s.id;
      }
    }
    if (best !== null) return best;
    for (const b of this.sim.state.buildings.values()) {
      if (!this.sim.areHostile(b.owner, this.localPlayer)) continue;
      const fp = footprintOf(buildingDef(b.defId), b.rot);
      if (
        Math.abs(world.x - b.pos.x) <= fp.w / 2 + 0.5 &&
        Math.abs(world.y - b.pos.y) <= fp.h / 2 + 0.5
      )
        return b.id;
    }
    return null;
  }

  /** Context-sensitive right click (§25.4). */
  private contextCommand(world: Vec2): void {
    // workers selected → repair friendly damaged structure or retreat to core
    if (this.selection.workers.length > 0) {
      for (const b of this.sim.state.buildings.values()) {
        if (b.owner !== this.localPlayer) continue;
        const fp = footprintOf(buildingDef(b.defId), b.rot);
        const inside =
          Math.abs(world.x - b.pos.x) <= fp.w / 2 + 1 &&
          Math.abs(world.y - b.pos.y) <= fp.h / 2 + 1;
        if (inside) {
          if (b.isCore) {
            this.renderer.spawnOrderMarker(world, 'move');
            this.audio.order('withdraw', world, this.renderer.camTarget);
            this.dispatch({ kind: 'WorkerRetreat', player: 0, workerIds: [...this.selection.workers] });
          } else if (b.health < b.maxHealth) {
            this.renderer.spawnOrderMarker(world, 'repair');
            this.audio.order('repair', world, this.renderer.camTarget);
            this.dispatch({
              kind: 'RepairOrder',
              player: 0,
              workerIds: [...this.selection.workers],
              buildingId: b.id,
            });
          }
          return;
        }
      }
      return;
    }

    // A selected wall garrison is a direct context target: right-clicking it toggles it
    // off the battlements instead of feeding the click back into MountWall. The generous
    // world-space radius accounts for the model being rendered four metres above the
    // ground plane used for pointer projection.
    let mountedHit: EntId | null = null;
    let mountedHitD = 4.5;
    for (const id of this.selection.squads) {
      const squad = this.sim.state.squads.get(id);
      if (!squad || squad.mountedOnWall === null) continue;
      const d = dist(this.visualPosition(squad), world);
      if (d < mountedHitD) {
        mountedHitD = d;
        mountedHit = id;
      }
    }
    if (mountedHit !== null) {
      const squad = this.sim.state.squads.get(mountedHit)!;
      this.renderer.spawnOrderMarker(this.visualPosition(squad), 'withdraw');
      this.audio.order('withdraw', this.visualPosition(squad), this.renderer.camTarget);
      this.dispatch({ kind: 'Dismount', player: 0, squadIds: [mountedHit] });
      return;
    }

    if (this.selection.squads.length === 0) {
      // building selected → set rally
      if (this.selection.buildingId !== null) {
        this.renderer.spawnOrderMarker(world, 'rally');
        this.audio.order('rally', world, this.renderer.camTarget);
        this.dispatch({ kind: 'SetRally', player: 0, buildingId: this.selection.buildingId, pos: world });
      }
      return;
    }
    // squads selected:
    // own castle wall → mount ranged squads (building a wall is a deliberate act now:
    // left-click the foundation and confirm in the panel — no accidental right-click spends)
    for (const ruin of this.sim.state.ruins) {
      if (dist(ruin.pos, world) > 7 || ruin.buildingId === null) continue;
      const wall = this.sim.state.buildings.get(ruin.buildingId);
      if (wall && wall.owner === this.localPlayer && wall.state === 'active') {
        this.renderer.spawnOrderMarker(ruin.pos, 'mount');
        this.audio.order('mount', ruin.pos, this.renderer.camTarget);
        this.dispatch({ kind: 'MountWall', player: 0, squadIds: [...this.selection.squads], buildingId: wall.id });
        return;
      }
    }
    const enemy = this.pickEnemyEntity(world);
    if (enemy !== null) {
      this.issueCommand('attack', world, enemy);
      return;
    }
    // own boss → mount (if every selected squad can ride it) else escort
    for (const s of this.sim.state.squads.values()) {
      if (s.owner === this.localPlayer && s.isBoss && dist(this.visualPosition(s), world) < 3.5 && !this.selection.squads.includes(s.id)) {
        const slots = unitDef(s.defId).mountSlots ?? 0;
        const allMountable =
          slots > 0 &&
          this.selection.squads.every((id) => {
            const sq = this.sim.state.squads.get(id);
            return sq !== undefined && (unitDef(sq.defId).mountableOn?.includes(s.defId) ?? false);
          });
        if (allMountable) {
          this.renderer.spawnOrderMarker(this.visualPosition(s), 'mount');
          this.audio.order('mount', this.visualPosition(s), this.renderer.camTarget);
          this.dispatch({ kind: 'MountSquad', player: 0, squadIds: [...this.selection.squads], hostId: s.id });
        } else {
          this.issueCommand('escort', world, s.id);
        }
        return;
      }
    }
    // site → capture (if not owned)
    for (const site of this.sim.state.sites) {
      if (dist(site.pos, world) <= TUNING.captureRadius + 3 && (site.owner === -1 || this.sim.areHostile(site.owner, this.localPlayer))) {
        this.issueCommand('capture', site.pos);
        return;
      }
    }
    this.issueCommand('move', world);
  }

  // ---------------------------------------------------------------- HUD intents

  togglePause(): void {
    if (this.onlineRuntime) return;
    this.sim.state.paused = !this.sim.state.paused;
    this.audio.ui(this.sim.state.paused ? 'open' : 'close');
  }

  dispatch(command: SimCommand): void {
    const localCommand = { ...command, player: this.localPlayer } as SimCommand;
    if (this.onlineRuntime && isDebugCommand(localCommand)) {
      this.lastDenyReason = 'Cheats are disabled in online matches';
      this.lastDenyAt = performance.now() / 1000;
    } else if (this.onlineRuntime) this.onlineRuntime.dispatch(localCommand as PlayerCommand);
    else this.sim.dispatch(localCommand);
  }

  concede(): void {
    if (this.onlineRuntime) this.onlineRuntime.concede();
    else if (this.sim.state.phase !== 'ended') this.sim.eliminatePlayer(this.localPlayer);
  }

  cheat(power: number, essence: number): void {
    this.dispatch({ kind: 'CheatResources', player: 0, power, essence });
  }

  cheatAscension(): void {
    this.dispatch({ kind: 'CheatAscension', player: 0 });
  }

  epicBattle(): void {
    this.dispatch({ kind: 'EpicBattle', player: 0 });
    this.moveCameraTo({ x: this.sim.map.width / 2, y: this.sim.map.height / 2 });
  }

  /**
   * Jump the camera. These are deliberate cross-map teleports — minimap clicks, F-to-Core, and
   * clicking an alert — so they snap rather than easing: gliding hundreds of metres would read
   * as lag, not as smoothness. Continuous pan and zoom are the things that ease.
   */
  moveCameraTo(pos: Vec2): void {
    this.renderer.camTarget.x = pos.x;
    this.renderer.camTarget.y = pos.y;
    this.renderer.snapCameraToTarget();
  }

  private visualPosition(entity: { pos: Vec2; prevPos: Vec2 }): Vec2 {
    return {
      x: entity.prevPos.x + (entity.pos.x - entity.prevPos.x) * this.renderAlpha,
      y: entity.prevPos.y + (entity.pos.y - entity.prevPos.y) * this.renderAlpha,
    };
  }
}

export function randomMatchSeed(): number {
  const values = new Uint32Array(1);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(values);
    return values[0] & 0x7fffffff;
  }
  return (Date.now() % 2147483647) >>> 0;
}

export { vec };
